import { memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isNil } from 'lodash';
// Import Types
import type { CampaignAwardCurrencyListItemUIModel } from './types';
// Import Store
import { useAppSelector } from 'store';
import { selectCurrencyRates } from 'store/slices/currencyRates';
import { selectUserSettingsCurrencyId } from 'store/slices/userData';
// Import Components
import CampaignBonusReceivedPlayers from '../CampaignBonusReceivedPlayers';
import CampaignReportBonusCalculationLogic from './CampaignReportBonusCalculationLogic';
import CampaignReportBonusCountsByStatuses from './CampaignReportBonusCountsByStatuses';
import CampaignBonusSummaryByCurrencyList from './CampaignBonusSummaryByCurrencyList';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useToaster from 'hooks/UseToaster';
// Import Services
import { CampaignHttpService } from 'services/http';
import { parseCampaignAwardDetailsToUIModel } from './service';
// Import Constants
import { CampaignType } from 'constants/common';

const CampaignBonus = (props) => {
    const { campaignId, campaignBonus, bonusInfo, startDate, endDate, isLoadingCampaign, campaignType, isActive } =
        props;
    const { doPostRequest, doGetRequest } = useRequest();
    const { showToaster } = useToaster();
    const [isLoadingBonusReceivedPlayers, setIsLoadingBonusReceivedPlayers] = useState(true);
    const [isLoadingBonusSummaryByCurrency, setIsLoadingBonusSummaryByCurrency] = useState(true);
    const [isRequestBodyChanged, setIsRequestBodyChanged] = useState(false);
    const [bonusReceivedPlayers, setBonusReceivedPlayers] = useState({});
    const [bonusSummaryByCurrency, setBonusSummaryByCurrency] = useState<CampaignAwardCurrencyListItemUIModel[]>([]);
    const [bonusTotalsData, setBonusTotalsData] = useState<AnyObject>({});
    const [isFileLoading, setIsFileLoading] = useState(false);

    const currencyRates = useAppSelector(selectCurrencyRates);
    const userCurrencyId = useAppSelector(selectUserSettingsCurrencyId);

    const { getCampaignBonusesReportRequest, getCampaignAwardDetailsRequest } = useCancelableRequests(
        {
            getCampaignBonusesReportRequest: CampaignHttpService.getCampaignBonusesReport,
            getCampaignAwardDetailsRequest: CampaignHttpService.getCampaignAwardDetails,
        },
        { takeLatest: ['getCampaignBonusesReportRequest', 'getCampaignAwardDetailsRequest'] },
    );

    // kept out of the hook: a file download must not be aborted when the user leaves the page
    const { downloadBonusReportRequest } = useMemo(
        () => ({
            // eslint-disable-next-line no-restricted-syntax -- download must survive unmount
            downloadBonusReportRequest: CampaignHttpService.downloadBonusReport(),
        }),
        [],
    );

    useEffect(() => {
        if (
            isLoadingCampaign ||
            !isActive ||
            !isRequestBodyChanged ||
            isEmpty(currencyRates) ||
            isNil(userCurrencyId)
        ) {
            return;
        }
        setIsRequestBodyChanged(false);

        setIsLoadingBonusReceivedPlayers(true);
        doPostRequest(getCampaignBonusesReportRequest.request, {
            requestBody: {
                CampaignId: campaignId,
                StartDate: startDate,
                EndDate: endDate,
                CampaignType: campaignType,
            },
            successCallback: (Data) => {
                setBonusReceivedPlayers(Data);
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsLoadingBonusReceivedPlayers(false);
            }
        });

        setIsLoadingBonusSummaryByCurrency(true);
        doPostRequest(getCampaignAwardDetailsRequest.request, {
            requestBody: {
                CampaignId: campaignId,
                StartDate: startDate,
                EndDate: endDate,
                CampaignType: campaignType,
            },
            successCallback: (Data) => {
                const parsedData = parseCampaignAwardDetailsToUIModel(Data, currencyRates, userCurrencyId);

                setBonusSummaryByCurrency(parsedData.tableData);
                setBonusTotalsData(parsedData.totalsData);
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsLoadingBonusSummaryByCurrency(false);
            }
        });
    }, [
        campaignId,
        startDate,
        endDate,
        isLoadingCampaign,
        isActive,
        isRequestBodyChanged,
        doPostRequest,
        getCampaignBonusesReportRequest,
        campaignType,
        doGetRequest,
        getCampaignAwardDetailsRequest,
        currencyRates,
        userCurrencyId,
    ]);

    useEffect(() => {
        setIsRequestBodyChanged(true);
    }, [campaignId, startDate, endDate]);

    const getRequestBody = () => {
        setIsFileLoading(true);
        return {
            CampaignId: campaignId,
            StartDate: startDate,
            EndDate: endDate,
            ReportType: 0,
            CampaignType: campaignType,
        };
    };

    const downloadAllOrSendEmailClickHandler = (isDownload) => {
        const requestBody = getRequestBody();
        return doPostRequest(downloadBonusReportRequest.request, {
            requestBody,
            queryString: { isDownload },
            isShowError: false,
            successCallback: (data) => {
                isDownload && window.open(data?.Url);
            },
        }).then(({ AlertType, AlertMessage, IsCanceled }) => {
            // Aborted on unmount: no toaster
            if (IsCanceled) return;
            showToaster(AlertType, AlertMessage);
            setIsFileLoading(false);
        });
    };

    return (
        <div className="campaign-bonus-wrap">
            <div className="campaign-bonus-calculation-and-currency-container">
                <div className="campaign-bonus-sub-container">
                    <CampaignReportBonusCalculationLogic
                        campaignBonus={campaignBonus}
                        bonusInfo={bonusInfo}
                        isLoading={isLoadingCampaign}
                    />
                    <CampaignReportBonusCountsByStatuses
                        countsByStatuses={bonusTotalsData.countsByStatuses ?? []}
                        totalAmountInUserCurrency={bonusTotalsData.totalAmountInUserCurrency}
                        userCurrencyId={userCurrencyId}
                        isLoading={isLoadingBonusSummaryByCurrency}
                    />
                </div>
                <div className="campaign-bonus-sub-container">
                    <CampaignBonusSummaryByCurrencyList
                        isLoading={isLoadingBonusSummaryByCurrency}
                        data={bonusSummaryByCurrency}
                        userCurrencyId={userCurrencyId}
                    />
                </div>
            </div>
            <CampaignBonusReceivedPlayers
                isLoading={isLoadingBonusReceivedPlayers}
                data={bonusReceivedPlayers}
                downloadAllClickHandler={() => downloadAllOrSendEmailClickHandler(true)}
                sendToEmailClickHandler={() => downloadAllOrSendEmailClickHandler(false)}
                isFileLoading={isFileLoading}
            />
        </div>
    );
};

CampaignBonus.propTypes = {
    campaignId: PropTypes.string.isRequired,
    campaignBonus: PropTypes.object,
    bonusInfo: PropTypes.object,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    isLoadingCampaign: PropTypes.bool.isRequired,
    campaignType: PropTypes.string,
    bonusCountData: PropTypes.object,
    isActive: PropTypes.bool,
};

CampaignBonus.defaultProps = {
    campaignType: CampaignType.AddHoc,
    isActive: true,
};

export default memo(CampaignBonus);
